/**
 * One review card (pedagogy spec §6.3.4 "Review card"), top to bottom:
 *
 *   1. the front;
 *   2. "Ditt svar först", a single-line input (typed recall is component state only, never stored);
 *      typing is encouraged but optional (ruling 4, slice C);
 *   3. how sure the student is, three buttons (Osäker, Ganska säker, Helt säker = 2, 3, 4 on the
 *      1-4 scale) that turn the card over and carry that confidence to the review request. They
 *      work with or without typed text (ruling 4): a confidence is what the flip needs. "Jag vet
 *      inte" turns the card over without a confidence;
 *   4. after the flip: the back, "Du skrev …" and the four SM-2 rating buttons. The confidence
 *      never changes which rating is offered or what it does; scheduling stays `shared/sm2`.
 *
 * Presentational: `ReviewPage` owns the state, the focus moves and the keyboard.
 */
import { useId, type RefObject } from "react";
import type { Rating } from "@shared/sm2";
import { Localized } from "@client/components/Localized";
import { Button, Card, Input, Kbd } from "@client/components/ui";
import { useT } from "@client/i18n";
import type { PreviewDto, RecallConfidence } from "./types";
import type { SessionCard } from "./session";

export const RATING_ORDER: Rating[] = ["again", "hard", "good", "easy"];
export const RATING_KEYS: Record<string, Rating> = {
  "1": "again",
  "2": "hard",
  "3": "good",
  "4": "easy",
};
/** The three confidence buttons and their keys; the stored value is the D5 scale 2, 3, 4. */
export const CONFIDENCE_KEYS: Record<string, RecallConfidence> = { "1": 2, "2": 3, "3": 4 };
const CONFIDENCE_BUTTONS = Object.entries(CONFIDENCE_KEYS).map(([key, level]) => ({ key, level }));

function previewLabel(t: ReturnType<typeof useT>, preview: PreviewDto | undefined): string {
  if (!preview) return "";
  return preview.minutes !== undefined
    ? t("flashcards.review.previewMinutes", { count: preview.minutes })
    : t("flashcards.review.previewDays", { count: preview.days });
}

export interface ReviewCardProps {
  card: SessionCard;
  flipped: boolean;
  recall: string;
  onRecall: (value: string) => void;
  /** A confidence button (the level) or "Jag vet inte" (null): turns the card over. */
  onChoose: (level: RecallConfidence | null) => void;
  onRate: (rating: Rating) => void;
  rating: boolean;
  frontRef: RefObject<HTMLDivElement>;
  inputRef: RefObject<HTMLInputElement>;
  /** The confidence group container: Enter in the input lands here, so digits choose at once. */
  confidenceRef: RefObject<HTMLDivElement>;
  /** The container of the back and the rating buttons: focused after the flip. */
  revealRef: RefObject<HTMLDivElement>;
}

export function ReviewCard({
  card,
  flipped,
  recall,
  onRecall,
  onChoose,
  onRate,
  rating: ratingPending,
  frontRef,
  inputRef,
  confidenceRef,
  revealRef,
}: ReviewCardProps) {
  const t = useT();
  const ids = useId();
  const cardHeadingId = `${ids}-card`;
  const frontId = `${ids}-front`;
  const inputId = `${ids}-recall`;
  const legendId = `${ids}-confidence`;
  const backHeadingId = `${ids}-back`;
  const hasText = recall.trim().length > 0;

  return (
    <Card aria-labelledby={cardHeadingId} className="min-h-[10rem]">
      <h2 id={cardHeadingId} className="sr-only">
        {t("flashcards.review.cardHeading")}
      </h2>
      <div ref={frontRef} id={frontId} tabIndex={-1} className="outline-none">
        <Localized value={card.front} />
      </div>

      {!flipped ? (
        <>
          <Input
            ref={inputRef}
            id={inputId}
            containerClassName="mt-4"
            label={t("flashcards.review.recall.label")}
            hint={t("flashcards.review.recall.help")}
            // The question is part of the description, so the input announces it even when
            // focus lands here straight from the previous card.
            aria-describedby={`${inputId}-hint ${frontId}`}
            value={recall}
            onChange={(event) => onRecall(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                confidenceRef.current?.focus();
              } else if (event.key === "Escape") {
                event.currentTarget.blur();
              }
            }}
            autoComplete="off"
            enterKeyHint="next"
          />

          <div
            ref={confidenceRef}
            role="group"
            aria-labelledby={legendId}
            tabIndex={-1}
            className="mt-4 outline-none"
          >
            <p id={legendId} className="mb-2 text-sm font-medium text-ink-700 dark:text-ink-200">
              {t("common.confidence.question")}
            </p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              {CONFIDENCE_BUTTONS.map(({ key, level }) => (
                <Button
                  key={level}
                  variant="secondary"
                  className="max-sm:min-h-11"
                  aria-keyshortcuts={key}
                  onClick={() => onChoose(level)}
                >
                  {t(`common.confidence.${level}`)} <Kbd className="ml-1">{key}</Kbd>
                </Button>
              ))}
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-ink-500 dark:text-ink-400">
              {t("flashcards.review.flipHint")}
            </p>
            <Button
              variant="ghost"
              size="sm"
              className="max-sm:min-h-11"
              onClick={() => onChoose(null)}
            >
              {t("flashcards.review.dontKnow")}
            </Button>
          </div>
        </>
      ) : (
        // Focus lands on this container after the flip: it is named by the back heading, so a
        // screen reader reads "Baksida, <the answer>" in one go instead of just the heading.
        <div
          ref={revealRef}
          role="group"
          aria-labelledby={backHeadingId}
          tabIndex={-1}
          className="mt-4 border-t border-ink-200 pt-4 outline-none dark:border-ink-800"
        >
          <h3 id={backHeadingId} className="sr-only">
            {t("flashcards.review.backHeading")}
          </h3>
          <Localized value={card.back} />
          {hasText && (
            <p className="mt-3 text-sm text-ink-600 dark:text-ink-400">
              {t("flashcards.review.recall.yours")}:{" "}
              <span className="whitespace-pre-wrap break-words text-ink-800 dark:text-ink-100">
                {recall}
              </span>
            </p>
          )}
          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {RATING_ORDER.map((rating, index) => {
              const preview = previewLabel(
                t,
                card.previews.find((p) => p.rating === rating),
              );
              return (
                <Button
                  key={rating}
                  variant={rating === "again" ? "danger" : "secondary"}
                  onClick={() => onRate(rating)}
                  loading={ratingPending}
                  // The visible label runs "Igen" and its <Kbd> digit straight into the preview
                  // line with no separator in the computed accessible name ("Igen 110 min") —
                  // an explicit label with a real space fixes it and doubles as the shortcut hint.
                  aria-label={`${t(`flashcards.review.rating.${rating}`)} ${preview}`.trim()}
                  aria-keyshortcuts={String(index + 1)}
                >
                  <span className="flex flex-col items-center">
                    <span>
                      {t(`flashcards.review.rating.${rating}`)}{" "}
                      <Kbd className="ml-1">{index + 1}</Kbd>
                    </span>
                    <span className="text-xs">{preview}</span>
                  </span>
                </Button>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
}
